import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Building2, UserRound, MonitorSmartphone, ToggleRight, Check, Eye, EyeOff, ShieldCheck, AlertCircle, Sparkles, Banknote, ListChecks } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { GROUPS, ACTIVITIES, ACTIVITY_TO_MODES, ACTIVITY_LABELS } from "@/lib/activityCatalog";

const TEMPLATES = [
  { id: "clone_demo", label: "Copia listino esempio (consigliato)" },
  { id: "blank", label: "Parti senza articoli" },
];

const MODULES = [
  { id: "cassa", label: "Cassa", hint: "POS, scontrini e chiusure" },
  { id: "magazzino", label: "Magazzino", hint: "Giacenze e movimenti" },
  { id: "multi_punto", label: "Multi-Punto Vendita", hint: "Più sedi, casse e magazzini separati" },
  { id: "reminder", label: "Reminder", hint: "Promemoria WhatsApp / SMS" },
  { id: "operatori", label: "Operatori", hint: "Staff, PIN e report operatori" },
  { id: "palmare", label: "Palmare sala", hint: "Comande cameriere (bar / ristorante)" },
];

const ADMIN_ACTIVITIES = {
  ...ACTIVITIES,
  negozi: [
    ...ACTIVITIES.negozi.slice(0, 2),
    { id: "entrambe", label: "Negozio Animali + Toelettatura", icon: ACTIVITIES.negozi[1].icon },
    ...ACTIVITIES.negozi.slice(2),
  ],
  eventi: [
    ...ACTIVITIES.eventi,
    { id: "alloggio_dedicato", label: "Alberghiero dedicato", icon: ACTIVITIES.eventi[2].icon },
  ],
};

const RESTO = new Set(["bar", "ristorante", "pizzeria"]);

const plusOneYear = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

const formatEur = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
};

const formatItDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

const emptyForm = () => ({
  businessName: "",
  vatNumber: "",
  address: "",
  ownerFirstName: "",
  ownerLastName: "",
  ownerEmail: "",
  ownerPassword: "",
  activityGroup: "servizi",
  activityType: "toelettatura",
  demoTemplate: "clone_demo",
  agreedAmount: "",
  licenseExpiresAt: plusOneYear(),
  modules: {
    cassa: true,
    magazzino: true,
    multi_punto: false,
    reminder: true,
    operatori: true,
    palmare: false,
  },
});

function Field({ id, label, type = "text", value, onChange, placeholder, required, autoComplete, children, ...rest }) {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-2 block text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">{label}</span>
      {children || (
        <input
          id={id}
          data-testid={`sa-field-${id}`}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
          className="h-12 w-full rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none transition focus:border-emerald-500/60 focus:ring-4 focus:ring-emerald-500/10"
          {...rest}
        />
      )}
    </label>
  );
}

function Section({ icon: Icon, kicker, title, children }) {
  return (
    <section className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-6 lg:p-7">
      <div className="mb-6 flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400">
          <Icon className="h-5 w-5" strokeWidth={1.8} />
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-500/80">{kicker}</div>
          <h2 className="font-heading text-lg font-semibold tracking-tight text-zinc-100">{title}</h2>
        </div>
      </div>
      {children}
    </section>
  );
}

export default function SuperAdmin() {
  const location = useLocation();
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [review, setReview] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const req = location.state?.fromRequest;
    if (!req) return;
    setForm((f) => ({
      ...f,
      businessName: req.business_name || f.businessName,
      vatNumber: req.vat_number || f.vatNumber,
      address: req.address || f.address,
      ownerEmail: req.email || req.owner_email || f.ownerEmail,
      ownerFirstName: req.contact_name?.split(" ")[0] || f.ownerFirstName,
      ownerLastName: req.contact_name?.split(" ").slice(1).join(" ") || f.ownerLastName,
    }));
  }, [location.state]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const pickActivity = (id) => {
    const resto = RESTO.has(id);
    setForm((f) => ({
      ...f,
      activityType: id,
      modules: { ...(f.modules || {}), palmare: resto },
    }));
  };

  const toggleModule = (id) => {
    setForm((f) => ({
      ...f,
      modules: { ...(f.modules || {}), [id]: !f.modules?.[id] },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);
    if (!review) {
      setReview(true);
      return;
    }
    setSubmitting(true);
    const modes = ACTIVITY_TO_MODES[form.activityType] || ["toelettatura"];
    const amount = parseFloat(String(form.agreedAmount).replace(",", "."));
    const payload = {
      business_name: form.businessName.trim(),
      vat_number: form.vatNumber.trim(),
      address: form.address.trim(),
      owner_name: `${form.ownerFirstName.trim()} ${form.ownerLastName.trim()}`.trim(),
      owner_first_name: form.ownerFirstName.trim(),
      owner_last_name: form.ownerLastName.trim(),
      owner_email: form.ownerEmail.trim().toLowerCase(),
      owner_password: form.ownerPassword,
      demo_template: form.demoTemplate,
      clone_from_demo: form.demoTemplate === "clone_demo",
      modules: form.modules,
      activity_type: form.activityType,
      modes,
      license_expires_at: form.licenseExpiresAt || null,
      agreed_amount_eur: Number.isFinite(amount) ? amount : null,
    };
    try {
      const r = await api.post("/admin/clients/create", payload);
      const cloned = r.data?.cloned_from_demo;
      const euro = Number.isFinite(amount) ? ` · ${formatEur(amount)}` : "";
      const scad = form.licenseExpiresAt ? ` · scade ${formatItDate(form.licenseExpiresAt)}` : "";
      setFeedback({
        type: "success",
        text: cloned
          ? `Cliente «${payload.business_name}» creato (${ACTIVITY_LABELS[payload.activity_type] || payload.activity_type})${euro}${scad}. Listino esempio copiato. Login: ${payload.owner_email}`
          : `Cliente «${payload.business_name}» creato (${ACTIVITY_LABELS[payload.activity_type] || payload.activity_type})${euro}${scad}. Login: ${payload.owner_email}`,
      });
      setForm(emptyForm());
      setReview(false);
    } catch (err) {
      setFeedback({
        type: "error",
        text: formatApiError(err, "Impossibile creare il cliente. Riprova."),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-full bg-zinc-950 text-zinc-200">
      <div className="mx-auto max-w-3xl px-6 py-10 lg:px-10 lg:py-12">
        <header className="mb-8">
          <div className="mb-3 text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-500">SuperAdmin</div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-white lg:text-4xl">Nuovo cliente</h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-zinc-400">
            Unica attivazione: chi è, che attività fa, listino di avvio. Poi i moduli. La cassa che vede il titolare dipende dall&apos;attività, non dal clone.
          </p>
        </header>

        {feedback && (
          <div
            data-testid="sa-feedback"
            role="status"
            className={`mb-6 flex items-start gap-3 rounded-xl border px-4 py-3.5 text-sm ${
              feedback.type === "success"
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/30 bg-red-500/10 text-red-200"
            }`}
          >
            {feedback.type === "success" ? (
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
            ) : (
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
            )}
            <p>{feedback.text}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="sa-create-client-form">
          <Section icon={Building2} kicker="01" title="Dati anagrafici">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field id="businessName" label="Ragione sociale" value={form.businessName} onChange={setField("businessName")} placeholder="Es. Pet Village Srl" required />
              </div>
              <Field id="vatNumber" label="Partita IVA" value={form.vatNumber} onChange={setField("vatNumber")} placeholder="IT01234567890" required />
              <div className="sm:col-span-2">
                <Field id="address" label="Indirizzo completo" value={form.address} onChange={setField("address")} placeholder="Via Roma 12, 20100 Milano (MI)" required />
              </div>
            </div>
          </Section>

          <Section icon={UserRound} kicker="02" title="Operatore principale">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="ownerFirstName" label="Nome" value={form.ownerFirstName} onChange={setField("ownerFirstName")} placeholder="Mario" required autoComplete="given-name" />
              <Field id="ownerLastName" label="Cognome" value={form.ownerLastName} onChange={setField("ownerLastName")} placeholder="Rossi" required autoComplete="family-name" />
              <Field id="ownerEmail" label="Email (login)" type="email" value={form.ownerEmail} onChange={setField("ownerEmail")} placeholder="mario@azienda.it" required autoComplete="off" />
              <Field id="ownerPassword" label="Password" required>
                <div className="relative">
                  <input
                    id="ownerPassword"
                    data-testid="sa-field-ownerPassword"
                    type={showPassword ? "text" : "password"}
                    value={form.ownerPassword}
                    onChange={setField("ownerPassword")}
                    placeholder="Scegli una password sicura"
                    required
                    autoComplete="new-password"
                    className="h-12 w-full rounded-lg border border-zinc-800 bg-zinc-950 px-4 pr-12 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none transition focus:border-emerald-500/60 focus:ring-4 focus:ring-emerald-500/10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
                    aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </Field>
            </div>
          </Section>

          <Section icon={Sparkles} kicker="03" title="Attività del locale">
            <p className="text-xs text-zinc-500 mb-4">Qui si decide la cassa (toelettatura, bar, ristorante…). Il clone copia solo il listino di questa attività.</p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(Array.isArray(GROUPS) ? GROUPS : []).map((g) => (
                <button
                  key={g.id}
                  type="button"
                  data-testid={`sa-group-${g.id}`}
                  onClick={() => setForm((f) => ({ ...f, activityGroup: g.id }))}
                  className={`text-xs px-3 h-8 rounded-full border font-medium transition ${form.activityGroup === g.id ? "border-emerald-500 bg-emerald-500/15 text-emerald-200" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(ADMIN_ACTIVITIES[form.activityGroup] || []).map((a) => {
                const Icon = a.icon;
                const on = form.activityType === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`sa-activity-${a.id}`}
                    onClick={() => pickActivity(a.id)}
                    className={`p-3 rounded-lg border text-left text-sm transition ${on ? "border-emerald-500 bg-emerald-500/10 text-white" : "border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}
                  >
                    <Icon className="w-4 h-4 mb-1 opacity-80" strokeWidth={1.8} />
                    {a.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-zinc-500 mt-3" data-testid="sa-activity-hint">
              Selezionata: <strong className="text-zinc-200">{ACTIVITY_LABELS[form.activityType] || form.activityType}</strong>
              {" · "}cassa: {(ACTIVITY_TO_MODES[form.activityType] || []).join(", ") || "—"}
            </p>
          </Section>

          <Section icon={MonitorSmartphone} kicker="04" title="Listino di avvio">
            <p className="text-xs text-zinc-500 mb-3">Non è il tipo di cassa. È solo se parti con articoli di esempio o vuoto.</p>
            <Field id="demoTemplate" label="Listino">
              <select
                id="demoTemplate"
                data-testid="sa-field-demoTemplate"
                value={form.demoTemplate}
                onChange={setField("demoTemplate")}
                className="h-12 w-full appearance-none rounded-lg border border-zinc-800 bg-zinc-950 bg-[length:12px] bg-[right_1rem_center] bg-no-repeat px-4 text-sm text-zinc-100 outline-none transition focus:border-emerald-500/60 focus:ring-4 focus:ring-emerald-500/10"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' fill='none'%3E%3Cpath d='M1 1.5 6 6.5 11 1.5' stroke='%2334d399' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
                }}
              >
                {(TEMPLATES || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
          </Section>

          <Section icon={ToggleRight} kicker="05" title="Moduli">
            <ul className="divide-y divide-zinc-800 rounded-lg border border-zinc-800">
              {(MODULES || []).map((m) => {
                const on = !!form.modules?.[m.id];
                return (
                  <li key={m.id} className="flex items-center justify-between gap-4 px-4 py-3.5">
                    <div>
                      <div className="text-sm font-medium text-zinc-100">{m.label}</div>
                      <div className="text-xs text-zinc-500">{m.hint}</div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      data-testid={`sa-module-${m.id}`}
                      onClick={() => toggleModule(m.id)}
                      className={`relative h-7 w-12 shrink-0 rounded-full transition ${on ? "bg-emerald-500" : "bg-zinc-700"}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow transition ${on ? "translate-x-5" : ""}`}>
                        {on && <Check className="h-3 w-3 text-emerald-600" strokeWidth={3} />}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Section>

          <Section icon={Banknote} kicker="06" title="Accordo commerciale">
            <p className="text-xs text-zinc-500 mb-4">Quanto avete pattuito e fino a quando è attiva la licenza. Niente piani core/pro: solo importo e data.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="agreedAmount" label="Importo pattuito (€)" type="number" value={form.agreedAmount} onChange={setField("agreedAmount")} placeholder="es. 49 oppure 490" required min="0" step="0.01" />
              <Field id="licenseExpiresAt" label="Scadenza licenza" type="date" value={form.licenseExpiresAt} onChange={setField("licenseExpiresAt")} required />
            </div>
          </Section>

          {review && (
            <Section icon={ListChecks} kicker="07" title="Controlla prima di creare">
              <dl className="grid gap-3 text-sm sm:grid-cols-2" data-testid="sa-review-summary">
                <div className="sm:col-span-2">
                  <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Cliente</dt>
                  <dd className="mt-1 text-zinc-100">{form.businessName} · P.IVA {form.vatNumber}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Login</dt>
                  <dd className="mt-1 text-zinc-100">{form.ownerEmail}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Attività / cassa</dt>
                  <dd className="mt-1 text-zinc-100">{ACTIVITY_LABELS[form.activityType] || form.activityType}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Listino</dt>
                  <dd className="mt-1 text-zinc-100">{form.demoTemplate === "clone_demo" ? "Esempio copiato" : "Vuoto"}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Importo</dt>
                  <dd className="mt-1 text-zinc-100">{formatEur(parseFloat(String(form.agreedAmount).replace(",", ".")))}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Scadenza</dt>
                  <dd className="mt-1 text-zinc-100">{formatItDate(form.licenseExpiresAt)}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Moduli</dt>
                  <dd className="mt-1 text-zinc-100">
                    {(MODULES || []).filter((m) => form.modules?.[m.id]).map((m) => m.label).join(" · ") || "—"}
                  </dd>
                </div>
              </dl>
            </Section>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            {review && (
              <button
                type="button"
                data-testid="sa-review-back"
                onClick={() => setReview(false)}
                className="inline-flex h-12 items-center justify-center rounded-lg border border-zinc-700 px-6 text-sm font-medium text-zinc-300 hover:border-zinc-500"
              >
                Modifica
              </button>
            )}
            <button
              type="submit"
              data-testid="sa-submit-btn"
              disabled={submitting}
              className="inline-flex h-12 items-center justify-center rounded-lg bg-emerald-500 px-8 text-sm font-semibold text-zinc-950 transition hover:bg-emerald-400 disabled:opacity-50"
            >
              {submitting ? "Creazione…" : review ? "Conferma e crea" : "Rivedi e crea"}
            </button>
          </div>
        </form>

        <ApkPublishPanel />
      </div>
    </div>
  );
}

function ApkPublishPanel() {
  const [version, setVersion] = useState("");
  const [apkUrl, setApkUrl] = useState("");
  const [changelog, setChangelog] = useState("");
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/app-update").then((r) => setCurrent(r.data)).catch(() => {});
  }, []);

  const publish = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/app-update", { version: version.trim(), apk_url: apkUrl.trim(), changelog });
      setCurrent(r.data);
      toast.success(`APK v${r.data.version} notificato ai tablet`);
      setVersion(""); setApkUrl(""); setChangelog("");
    } catch (err) {
      toast.error(formatApiError(err, "Pubblicazione APK fallita"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-10 rounded-xl border border-zinc-800 bg-zinc-900/60 p-6 lg:p-7" data-testid="sa-apk-panel">
      <div className="mb-2 text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-500/80">OTA Android</div>
      <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-1">Pubblica nuova versione APK</h2>
      <p className="text-sm text-zinc-500 mb-4">I tablet controllano all&apos;avvio <code className="text-zinc-400">/api/app/version-check</code> e scaricano l&apos;APK con un tap. Non servono app extra.</p>
      {current?.version && (
        <p className="text-xs text-emerald-400/90 mb-3">Pubblicata: v{current.version} · {current.apk_url}</p>
      )}
      <form onSubmit={publish} className="grid gap-3 sm:grid-cols-2">
        <input data-testid="sa-apk-version" required value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.0.2" className="h-11 rounded-lg border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100" />
        <input data-testid="sa-apk-url" required value={apkUrl} onChange={(e) => setApkUrl(e.target.value)} placeholder="https://api.veltaone.it/downloads/veltaone-latest.apk" className="h-11 rounded-lg border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 sm:col-span-2" />
        <textarea data-testid="sa-apk-changelog" value={changelog} onChange={(e) => setChangelog(e.target.value)} placeholder="Novità per il cliente…" rows={2} className="sm:col-span-2 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
        <button type="submit" data-testid="sa-apk-publish" disabled={busy} className="h-11 rounded-lg bg-emerald-500 px-5 text-sm font-semibold text-zinc-950 disabled:opacity-50">
          {busy ? "Pubblicazione…" : "Notifica aggiornamento"}
        </button>
      </form>
    </section>
  );
}
